import type { ValueOf } from 'type-fest';
import z from 'zod';

import { ChartOfAccountSchema } from 'app-components/flux/chart-of-accounts/chartOfAccountsTypes';

export const MERCHANT_ORDERS_DEFAULT_PAGE_SIZE = 100;

/**
 * The statuses the API actually returns. The service tracks twelve internal codes but
 * `OrderResourceAssembler` emits `OrderStatusEnum.getCustomerFacingStatus()` uppercased, which
 * collapses them into these four. Notably `pending` is a *failure* bucket (a retryable one), not
 * "not started".
 *
 *   processing  CREATED, FETCHING_CATEGORIES, PREPARING_*_EMBEDDINGS, CLASSIFYING
 *   pending     FETCHING_CATEGORIES_FAILED, PREPARING_*_EMBEDDINGS_FAILED, CLASSIFICATION_UNAVAILABLE
 *   failed      MISSING_CATEGORIES, FAILED
 *   completed   COMPLETED
 */
export const merchantOrderStatus = {
  processing: 'PROCESSING',
  pending: 'PENDING',
  failed: 'FAILED',
  completed: 'COMPLETED',
} as const;

export type MerchantOrderStatus = ValueOf<typeof merchantOrderStatus>;

/** Why an order produced no split. Reported per order so a low match rate is diagnosable. */
export const merchantOrderSkipReason = {
  /**
   * Not `COMPLETED`, so the items carry no categories yet. The API buckets its internal statuses,
   * so this cannot distinguish "still working" from "gave up".
   */
  notClassified: 'notClassified',
  /**
   * No grand total, or a zero one. An order covered entirely by a gift card nets to zero here,
   * which is right: nothing was charged, so no transaction can correspond to it.
   */
  noTotal: 'noTotal',
  /** No usable rows at all. A single row is fine — it becomes a categorized transaction. */
  noSplitRows: 'noSplitRows',
  noCandidateTransaction: 'noCandidateTransaction',
  /** The order fit more than one transaction. */
  ambiguousOrder: 'ambiguousOrder',
  /** The only fitting transaction also fit another order. */
  ambiguousTransaction: 'ambiguousTransaction',
  /** The matched transaction's memo already leads with this candidate's product name. */
  alreadyCategorized: 'alreadyCategorized',
} as const;

export type MerchantOrderSkipReason = ValueOf<typeof merchantOrderSkipReason>;

const toMoney = (value: number | string): number | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

const MoneySchema = z.union([z.number(), z.string()]).transform(toMoney).nullable().optional();

/**
 * Parsed as a free string rather than an enum so an unrecognised bucket cannot fail the whole page.
 * Compare against `merchantOrderStatus` instead of relying on the parse to reject unknowns.
 */
const MerchantOrderStatusSchema = z.string().nullable().optional();

/** `coa` arrives as `{ type, id, name }`; zod strips `name`, leaving Simplifi's own COA shape. */
export const MerchantOrderItemSchema = z.object({
  id: z.string().optional(),
  clientId: z.string().nullable().optional(),
  name: z.string(),
  amount: MoneySchema,
  quantity: z.union([z.number(), z.string()]).nullable().optional(),
  coa: ChartOfAccountSchema.nullable().optional(),
  /** The transaction this item was matched to. Written at insert; no update path exists yet. */
  transactionId: z.string().nullable().optional(),
});

export const MerchantOrderChargeSchema = z.object({
  name: z.string().nullable().optional(),
  amount: MoneySchema,
});

export const MerchantOrderPaymentSchema = z.object({
  lastFourDigit: z.union([z.number(), z.string()]).nullable().optional(),
  name: z.string().nullable().optional(),
  type: z.string().nullable().optional(),
  amount: MoneySchema,
});

export const MerchantOrderSchema = z.object({
  id: z.string().optional(),
  clientId: z.string().nullable().optional(),
  merchantId: z.union([z.number(), z.string()]).optional(),
  merchantName: z.string().nullable().optional(),
  createdAt: z.string().nullable().optional(),
  modifiedAt: z.string().nullable().optional(),
  userModifiedAt: z.string().nullable().optional(),
  totalAmountForProducts: MoneySchema,
  shippingFee: MoneySchema,
  deliveryFee: MoneySchema,
  additionalCharges: z.array(MerchantOrderChargeSchema).nullable().optional(),
  adjustmentsAmount: z.array(MerchantOrderChargeSchema).nullable().optional(),
  totalBeforeTax: MoneySchema,
  tax: MoneySchema,
  tip: MoneySchema,
  giftCardAmount: MoneySchema,
  grandTotal: MoneySchema,
  inStorePurchase: z.boolean().nullable().optional(),
  paymentInfo: z.array(MerchantOrderPaymentSchema).nullable().optional(),
  items: z.array(MerchantOrderItemSchema).nullable().optional(),
  orderDate: z.string(),
  orderId: z.string().nullable().optional(),
  merchantOrderId: z.string().nullable().optional(),
  payee: z.string().nullable().optional(),
  orderDetailsLink: z.string().nullable().optional(),
  status: MerchantOrderStatusSchema,
  statusMessage: z.string().nullable().optional(),
});

export const MerchantOrdersResponseSchema = z.object({
  resources: z.array(MerchantOrderSchema).default([]),
  metaData: z
    .object({
      pageSize: z.number().optional(),
      limit: z.number().optional(),
      offset: z.number().optional(),
      currentPage: z.number().optional(),
      totalPages: z.number().optional(),
      totalSize: z.number().optional(),
    })
    .passthrough()
    .optional(),
});

export const MerchantSchema = z.object({
  id: z.union([z.number(), z.string()]),
  name: z.string(),
});

/**
 * Unlike orders, the merchants endpoint answers with a bare array rather than a `resources`
 * envelope. Both are accepted so a future envelope would not break the caller.
 */
export const MerchantsResponseSchema = z
  .union([z.array(MerchantSchema), z.object({ resources: z.array(MerchantSchema).default([]) })])
  .transform((response) => (Array.isArray(response) ? response : response.resources));

export type MerchantOrderItem = z.infer<typeof MerchantOrderItemSchema>;
export type MerchantOrderCharge = z.infer<typeof MerchantOrderChargeSchema>;
export type MerchantOrderPayment = z.infer<typeof MerchantOrderPaymentSchema>;
export type MerchantOrder = z.infer<typeof MerchantOrderSchema>;
export type MerchantOrdersResponse = z.infer<typeof MerchantOrdersResponseSchema>;
export type Merchant = z.infer<typeof MerchantSchema>;

export type FetchMerchantOrdersParams = {
  limit?: number;
  offset?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  modifiedAfter?: string;
};

export const isClassifiedMerchantOrder = (order: MerchantOrder): boolean =>
  order.status === merchantOrderStatus.completed;
